import React from "react";
import { Round } from "../../types/tournament";
import { getRoundType } from "../../types/tournament";
import { MatchPairing } from "./MatchPairing";

interface RoundSectionProps {
  round: Round;
  showDivider?: boolean;
}

export const RoundSection: React.FC<RoundSectionProps> = ({
  round,
  showDivider = true,
}) => {
  const roundType = getRoundType(round.tokenCount);

  return (
    <>
      <div className="flex flex-col items-center space-y-4">
        {/* Round Title with progressive enhancement */}
        {roundType === "grand-finals" ? (
          <div className="relative flex flex-col items-center space-y-2">
            {/* Championship badge */}
            <div className="text-yellow-500 text-xs sm:text-sm font-bold tracking-[0.2em] sm:tracking-[0.3em] uppercase animate-pulse">
              ⚔️ Championship Match ⚔️
            </div>
            {/* Main title with dramatic styling - responsive */}
            <h2 className="relative text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-wider">
              <span className="absolute inset-0 blur-xl bg-gradient-to-r from-purple-600 to-cyan-600 bg-clip-text text-transparent opacity-70">
                {round.name}
              </span>
              <span className="relative bg-gradient-to-r from-yellow-300 via-yellow-500 to-yellow-300 bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(255,215,0,0.5)]">
                {round.name}
              </span>
            </h2>
            {/* Decorative lines */}
            <div className="flex items-center space-x-4">
              <div className="w-20 h-px bg-gradient-to-r from-transparent to-yellow-500" />
              <span className="text-yellow-500">👑</span>
              <div className="w-20 h-px bg-gradient-to-l from-transparent to-yellow-500" />
            </div>
          </div>
        ) : roundType === "semi-finals" ? (
          <div className="relative flex flex-col items-center">
            {/* Semi-finals with tasteful enhancement */}
            <h2 className="relative text-2xl sm:text-3xl font-bold uppercase tracking-wider">
              <span className="absolute inset-0 blur-md text-purple-400 opacity-50">
                {round.name}
              </span>
              <span className="relative text-white drop-shadow-[0_0_20px_rgba(168,85,247,0.4)]">
                {round.name}
              </span>
            </h2>
            {/* Elite 4 tagline with enhanced bracketing */}
            <div className="flex items-center mt-2">
              <span className="text-purple-400/60 text-lg">◆</span>
              <div className="w-16 h-px bg-gradient-to-r from-purple-400/40 to-transparent mx-2" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-purple-400">
                Elite 4
              </span>
              <div className="w-16 h-px bg-gradient-to-l from-purple-400/40 to-transparent mx-2" />
              <span className="text-purple-400/60 text-lg">◆</span>
            </div>
          </div>
        ) : roundType === "quarter-finals" ? (
          <div className="relative flex flex-col items-center">
            {/* Quarter-finals with moderate styling */}
            <h2 className="relative text-xl sm:text-2xl font-bold uppercase tracking-wider">
              <span className="absolute inset-0 blur-md text-cyan-400 opacity-50">
                {round.name}
              </span>
              <span className="relative text-white drop-shadow-[0_0_20px_rgba(6,182,212,0.4)]">
                {round.name}
              </span>
            </h2>
            {/* Decorative brackets */}
            <div className="flex items-center mt-2 text-cyan-400/50">
              <span className="text-xl">⟨</span>
              <div className="w-16 h-px bg-gradient-to-r from-cyan-400/50 to-transparent mx-2" />
              <span className="text-xs uppercase tracking-wider text-cyan-400/70">
                Top 8
              </span>
              <div className="w-16 h-px bg-gradient-to-l from-cyan-400/50 to-transparent mx-2" />
              <span className="text-xl">⟩</span>
            </div>
          </div>
        ) : (
          /* Standard rounds - clear but simple */
          <div className="relative">
            {round.tokenCount <= 16 ? (
              /* Round of 16 - enhanced visibility */
              <div className="flex flex-col items-center">
                <h2 className="text-lg sm:text-xl font-semibold uppercase tracking-wide text-white/80">
                  {round.name}
                </h2>
                <div className="w-16 h-px bg-white/20 mt-1" />
              </div>
            ) : (
              /* Early rounds - still readable */
              <h2 className="text-sm sm:text-base font-medium uppercase tracking-wide text-white/60">
                {round.name}
              </h2>
            )}
          </div>
        )}

        {/* Grand Finals - Special Layout */}
        {roundType === "grand-finals" && round.matches[0] && (
          <>
            <MatchPairing match={round.matches[0]} roundType={roundType} />
            <div className="text-cyan-400 text-lg font-semibold">
              Total Liquidity: $
              {Math.round(
                round.matches[0].tokenA!.liquidity +
                round.matches[0].tokenB!.liquidity
              ).toLocaleString()}
            </div>
          </>
        )}

        {/* Semi-Finals - Responsive Layout */}
        {roundType === "semi-finals" && (
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            {round.matches.map((match, index) => (
              <React.Fragment key={match.id}>
                <MatchPairing match={match} roundType={roundType} />
                {index < round.matches.length - 1 && (
                  <div className="w-px bg-white/20" />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Quarter-Finals - Responsive grid layout */}
        {roundType === "quarter-finals" && (
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {round.matches.map((match) => (
              <MatchPairing
                key={match.id}
                match={match}
                roundType={roundType}
              />
            ))}
          </div>
        )}

        {/* Standard Rounds - Responsive Grid */}
        {roundType === "standard" && (
          <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 max-w-6xl mx-auto">
            {round.matches.map((match) => (
              <MatchPairing
                key={match.id}
                match={match}
                roundType={roundType}
              />
            ))}
          </div>
        )}
      </div>

      {/* Divider */}
      {showDivider && (
        <div className="w-full h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      )}
    </>
  );
};
